import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { formatNumber, percent } from "../../utils/format.ts";

export interface Segment {
  key: string;
  label: string;
  value: number;
  // Any CSS color; theme variables keep dark mode right.
  color: string;
  to?: string;
}

/**
 * Part-to-whole in one thin bar, with a legend under it that names every
 * part, its count and share; each legend row opens the list behind it.
 */
export default function SegmentedBar({
  segments,
  emptyLabel = "Nothing yet",
}: {
  segments: Segment[];
  emptyLabel?: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "flex", gap: "2px", height: 10, borderRadius: 5, overflow: "hidden" }}>
        {total === 0 ? (
          <Box sx={{ flex: 1, bgcolor: "action.hover" }} />
        ) : (
          segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <Tooltip key={s.key} title={`${s.label}: ${formatNumber(s.value)} (${percent(s.value, total)})`}>
                <Box sx={{ flexGrow: s.value, flexBasis: 0, minWidth: 4, bgcolor: s.color }} />
              </Tooltip>
            ))
        )}
      </Box>
      {total === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Stack spacing={0.25}>
          {segments.map((s) => (
            <ButtonBase
              key={s.key}
              component={s.to ? Link : "div"}
              to={s.to}
              disabled={!s.to}
              sx={{
                justifyContent: "flex-start",
                gap: 1,
                px: 1,
                py: 0.75,
                mx: -1,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }}>
                {s.label}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {formatNumber(s.value)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  width: 40,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {percent(s.value, total)}
              </Typography>
            </ButtonBase>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
